import { ButtonERP } from "@finanedi.es/storybook"
import {
  faArrowLeft,
  faArrowsRotateReverse,
  faFileExport,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GenerarFicheroModelo190CommandProps } from "api/Commands/GenerarFicheroModelo190Command"
import { postDeclaracionModelo190 } from "api/DeclaracionModelo"
import {
  MapRespToDtoFn390,
  useDeclaracion190,
} from "api/DeclaracionModelo.hook"
import { MODELO_IMPUESTO } from "api/Types/ModeloImpuesto"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import Spinner from "common/components/Spinner"
import notification from "common/utils/notification"
import { TFunction } from "i18next"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"
import * as Yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import EjercicioDeclaracion from "../../EjercicioDeclaracion"
import { ModalPresentarModelo } from "../../ModalPresentarModelo"
import { PlantillaDeclaracion } from "../../PlantillaDeclaracion"
import PlantillaDeclaracionEstandar from "../../PlantillaDeclaracionEstandar"
import {
  Modelo190FormModel,
  postModelo190Adapter,
} from "../postModelo190.adapter"
import Perceptores190 from "./Perceptors190/Perceptores190"

const SECCIONES_TYPES = {
  DECLARANTE: 1,
  EJERCICIO: 2,
  RESUMEN_DATOS: 3,
  PERCEPTORES: 4,
}

const schemaModelo190 = Yup.object({
  declarante: Yup.object({
    nifDeclarante: Yup.string().required(),
    telefono: Yup.string().required(),
    nombre: Yup.string().required(),
    personaContacto: Yup.string().required(),
    email: Yup.string().email().required(),
  }),
  perceptores: Yup.array().of(
    Yup.object({
      clave: Yup.string().required(),
      subclave: Yup.string().required(),
    })
  ),
}).required()
export type Modelo190Form = Yup.InferType<typeof schemaModelo190>

const SECCIONES = (t: TFunction) => [
  {
    id: SECCIONES_TYPES.DECLARANTE,
    label: `${SECCIONES_TYPES.DECLARANTE} - ${t(
      "taxSummary.declaracion.declarante"
    )}`,
  },
  {
    id: SECCIONES_TYPES.EJERCICIO,
    label: `${SECCIONES_TYPES.EJERCICIO} - ${t(
      "taxSummary.declaracion.ejercicio"
    )}`,
  },
  {
    id: SECCIONES_TYPES.RESUMEN_DATOS,
    label: `${SECCIONES_TYPES.RESUMEN_DATOS} - ${t(
      "taxSummary.declaracion.summary"
    )}`,
  },
  {
    id: SECCIONES_TYPES.PERCEPTORES,
    label: `${SECCIONES_TYPES.PERCEPTORES} - ${t(
      "taxSummary.declaracion.perceptors"
    )}`,
  },
]

const dummyMapper: MapRespToDtoFn390 = () => ({})

const DeclaracionModelo190 = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [tabSelected, setSelectedTab] = useState(SECCIONES_TYPES.DECLARANTE)
  const [showModalPresentar, setShowModalPresentar] = useState<boolean>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const {
    activeCompany: { razonSocial, nif },
  } = useUsuarioEmpresaActiva()
  const { state } = useLocation() as { state?: { year?: number } }

  const {
    declaracion190Data,
    declaracion190DataIsLoading,
    declaracion190DataRefetch,
  } = useDeclaracion190({
    queryParams: {
      Anyo: Number(state?.year),
    },
    mapRespToDto: dummyMapper,
    razonSocial,
    nif,
    year: state?.year,
  })

  const defaultValues = {
    "01": declaracion190Data?.casilla01,
    "02": declaracion190Data?.casilla02,
    "03": declaracion190Data?.casilla03,
    declarante: {
      // Se envia false por defecto a pedido del back (Corcoles P):
      declaracionComplementaria: false,
      // Se envia false por defecto a pedido del back (Corcoles P):
      declaracionSustitutiva: false,
      nifDeclarante: nif,
      nombre: razonSocial ?? "",
    },
    ejercicio: state?.year,
    perceptores: declaracion190Data?.perceptores,
  }

  const {
    setValue,
    watch,
    reset,
    formState: { errors },
    trigger,
  } = useForm<$TSMigrationFixMe>({
    values: defaultValues,
    resolver: yupResolver(schemaModelo190),
    mode: "onChange",
    reValidateMode: "onChange",
  })
  const handlePresentarClick = async () => {
    const isValid = await trigger()
    if (!isValid) {
      notification.warning({
        body: t("notification.warningModelDeclaration"),
      })
      const declaranteValid = await trigger([
        "declarante.telefono",
        "declarante.personaContacto",
        "declarante.email",
      ])
      if (!declaranteValid) {
        setSelectedTab(SECCIONES_TYPES.DECLARANTE)
        return
      }

      const perceptoresValid = await trigger(["perceptores"])
      if (!perceptoresValid) {
        setSelectedTab(SECCIONES_TYPES.PERCEPTORES)
        return
      }
    }
    setShowModalPresentar(!showModalPresentar)
  }

  const values = watch()

  const handleUpdateClick = async () => {
    setIsUpdating(true)
    try {
      const { data: updatedData } = await declaracion190DataRefetch()
      if (updatedData) {
        reset(defaultValues)
        notification.success({
          body: `${t("taxSummary.declaracion.successNotification")}`,
        })
        setIsUpdating(false)
      }
    } catch (error) {
      setIsUpdating(false)
      notification.errorServidor()
    }
  }

  return (
    <div className='tw-w-full tw-h-full tw-overflow-hidden tw-bg-white tw-text-left'>
      <div className='tw-h-fit tw-p-3 tw-w-full tw-bg-white tw-shadow'>
        <div className='tw-h-11 tw-flex tw-flex-row tw-w-full tw-items-center tw-justify-between'>
          <div className='tw-flex tw-flex-row tw-gap-4 tw-items-center tw-h-full'>
            <ButtonERP
              customize={{ size: "md", type: "transparent" }}
              onClick={() => history.goBack()}
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
            />
            <span className='tw-rounded tw-h-fit tw-py-1 tw-px-2 tw-text-sm tw-text-white tw-bg-[#85A3CE]'>
              {MODELO_IMPUESTO.MODELO_190}
            </span>
            <span className='tw-text-base tw-font-medium tw-h-fit'>
              {t("modelo190.modal.title")} -{" "}
              {state?.year ?? new Date().getFullYear()}
            </span>
          </div>
          <div className='tw-flex tw-flex-row tw-gap-3'>
            <ButtonERP
              customize={{ size: "md", type: "transparent" }}
              label={`${t("taxSummary.declaracion.updateButton")}`}
              loading={isUpdating}
              disabled={isUpdating}
              icon={<FontAwesomeIcon icon={faArrowsRotateReverse} />}
              onClick={() => {
                handleUpdateClick()
              }}
            />
            <ButtonERP
              customize={{ size: "md", type: "primary" }}
              onClick={handlePresentarClick}
              icon={<FontAwesomeIcon icon={faFileExport} />}
              label={`${t("taxSummary.declaracion.presentar")}`}
            />
          </div>
        </div>
      </div>

      <div className='tw-w-full tw-h-[calc(100%-68px)] tw-flex tw-flex-row'>
        <div className='tw-w-2/5 tw-p-6 tw-flex tw-flex-col tw-items-start tw-border-r tw-border-neutral-25'>
          <p className='tw-font-medium tw-text-base'>
            {t("taxSummary.declaracion.secciones")}
          </p>
          <div className='tw-flex tw-flex-col tw-items-start tw-mt-4 tw-w-full tw-gap-1 tw-text-left'>
            {SECCIONES(t).map(({ id, label }) => (
              <p
                key={id}
                className={`tw-h-10 tw-w-full tw-px-4 hover:tw-bg-neutral-25 ${
                  tabSelected === id ? "tw-bg-neutral-25" : ""
                } tw-flex tw-items-center tw-cursor-pointer tw-rounded tw-truncate`}
                onClick={() => setSelectedTab(id)}
              >
                {label}
              </p>
            ))}
          </div>
        </div>

        <div className='tw-w-4/5 tw-flex tw-flex-col tw-items-start tw-overflow-y-auto'>
          <div className='tw-w-full tw-text-base tw-font-medium tw-flex tw-items-center tw-justify-start tw-px-6 tw-py-4'>
            {SECCIONES(t).find((s) => s.id === tabSelected)?.label}
          </div>
          {declaracion190DataIsLoading ? (
            <Spinner />
          ) : (
            <>
              {tabSelected === SECCIONES_TYPES.DECLARANTE && (
                <PlantillaDeclaracionEstandar
                  setValue={setValue}
                  fields={[
                    {
                      id: "row1",
                      inputsOne: [
                        {
                          label: t("taxSummary.declaracion.nif"),
                          code: "declarante.nifDeclarante",
                          value: values.declarante?.nifDeclarante,
                          inputType: "text",
                          mandatory: true,
                          error: errors?.declarante?.nifDeclarante
                            ?.message as string,
                        },
                        {
                          label: t("taxSummary.declaracion.telephone"),
                          code: "declarante.telefono",
                          value: values.declarante?.telefono,
                          inputType: "text",
                          mandatory: true,
                          error: errors?.declarante?.telefono
                            ?.message as string,
                        },
                        {
                          label: t("taxSummary.declaracion.name"),
                          code: "declarante.nombre",
                          value: values.declarante?.nombre,
                          inputType: "text",
                          mandatory: true,
                          error: errors.declarante?.nombre?.message as string,
                          colSpan: "tw-col-span-2",
                        },
                      ],
                    },
                    {
                      id: "row2",
                      inputsOne: [
                        {
                          label: t("taxSummary.declaracion.contact"),
                          code: "declarante.personaContacto",
                          value: values.declarante?.personaContacto,
                          inputType: "text",
                          mandatory: true,
                          error: errors.declarante?.personaContacto
                            ?.message as string,
                          colSpan: "tw-col-span-4",
                        },
                      ],
                    },
                    {
                      id: "row3",
                      inputsOne: [
                        {
                          label: t("modelo190.declarante.contactEmail"),
                          code: "declarante.email",
                          value: values.declarante?.email,
                          inputType: "text",
                          mandatory: true,
                          error: errors.declarante?.email?.message as string,
                          colSpan: "tw-col-span-4",
                        },
                      ],
                    },
                  ]}
                />
              )}
              {tabSelected === SECCIONES_TYPES.EJERCICIO && (
                <EjercicioDeclaracion ejercicio={state?.year} />
              )}
              {tabSelected === SECCIONES_TYPES.RESUMEN_DATOS && (
                <PlantillaDeclaracion
                  setValue={setValue}
                  isDeclaracion190
                  fields={[
                    {
                      label: t("modelo190.dataSummary.box1"),
                      code: "01",
                      value: values["01"],
                      disabled: false,
                      inputType: "numberGroup",
                    },
                    {
                      label: t("modelo190.dataSummary.box2"),
                      code: "02",
                      value: values["02"],
                      disabled: false,
                      inputType: "numberGroup",
                    },
                    {
                      label: t("modelo190.dataSummary.box3"),
                      code: "03",
                      value: values["03"],
                      disabled: false,
                      inputType: "numberGroup",
                    },
                  ]}
                />
              )}
              {tabSelected === SECCIONES_TYPES.PERCEPTORES && (
                <Perceptores190
                  setValue={setValue}
                  values={values}
                  errors={errors}
                />
              )}
              <span className='tw-text-xs tw-text-left tw-w-full tw-py-1 tw-px-6'>
                {t("taxSummary.declaracion.infoMsg")}
              </span>
            </>
          )}
        </div>
      </div>

      <ModalPresentarModelo<
        Modelo190FormModel,
        GenerarFicheroModelo190CommandProps
      >
        show={showModalPresentar}
        setShow={setShowModalPresentar}
        nombreDocumento='modelo190.txt'
        postDeclaracion={postDeclaracionModelo190}
        postDeclaracionAdapter={postModelo190Adapter}
        modalTitle='modelo190.modal.title'
        infoText='taxSummary.declaracion.modal.infoTextTwo'
        values={values}
      />
    </div>
  )
}
export default DeclaracionModelo190
